import { IVariableOperationsGpsBonusSetup } from "@/PayPlans/types/sections/variableOperationsGpsBonus";
import { TErrors, TPath } from "./validation";
import { IEligibilityRequirement } from "../../types/spiffSetup";
import { get, some } from "lodash";

const findError = (errors: TErrors, path: TPath) => {
	const ruleErrors = errors?.details?.find((item) => item.type === "array.unique");
	if (ruleErrors && path[0] === "rules") {
		return ruleErrors?.context?.details?.find(
			(item: { path: (number | string)[] }) => JSON.stringify(item?.path) === JSON.stringify(path.slice(1))
		);
	}
	return errors?.details?.find((item) => JSON.stringify(item?.path) === JSON.stringify(path));
};

const allowedGPSKeys: Array<keyof IVariableOperationsGpsBonusSetup> = [
	"_id",
	"newVehicles",
	"preOwned",
	"combined",
	"combineFranchises",
	"globalUnitCounts",
];
const allowedSalesTradesKeys: Array<keyof IEligibilityRequirement> = [
	"rules",
	"tiers",
	"applyTiersRetroactively",
	"genericValue",
	"pvrType",
	"franchises",
	"requiredStoreUnits",
	"requiredPvr",
];
const allowedDepartmentKeys: Array<keyof IEligibilityRequirement> = [
	"tiers",
	"applyTiersRetroactively",
	"selectedPayType",
	"selectedDepartment",
	"component",
];
const allowedOpCodesKeys: Array<keyof IEligibilityRequirement> = [
	"tiers",
	"applyTiersRetroactively",
	"selectedPayType",
	"selectedOperationCodes",
	"selectedTimeLapse",
	"component",
];

function filterByInterface<T>(data: any, allowedKeys: string[]): T {
	if (typeof data !== "object" || data === null) return {} as T;

	const result: any = {};

	for (const key of allowedKeys) {
		if (key in data) {
			result[key] = data[key];
		}
	}

	return result as T;
}

function hasPositiveBonus(data: IVariableOperationsGpsBonusSetup): boolean {
	// Helper that checks an array of makes (newVehicles or others)
	const checkMakes = (makes: any[]) =>
		some(makes, (make) => some(make.tiers, (tier) => some(tier.bonuses, (b) => get(b, "bonus.value", 0) > 0)));

	// newVehicles is always an array
	if (checkMakes(data.newVehicles)) return true;

	// preOwned is a single object
	if (data.preOwned && checkMakes([data.preOwned])) {
		return true;
	}

	// combined is a single object
	if (data.combined && checkMakes([data.combined])) {
		return true;
	}

	return false;
}

function getObjectsWithConfiguredCommission(data: any): any[] {
	const result: any[] = [];

	const hasConfiguredCommission = (makeObj: any) =>
		some(makeObj.tiers, (tier) => some(tier.commissions, (b) => get(b, "commission.value", 0) > 0));

	// newVehicles (array)
	data.newVehicles.forEach((makeObj: any) => {
		if (hasConfiguredCommission(makeObj)) {
			result.push(makeObj);
		}
	});

	// preOwned (single object)
	if (data.preOwned && hasConfiguredCommission(data.preOwned)) {
		result.push(data.preOwned);
	}

	// combined (single object)
	if (data.combined && hasConfiguredCommission(data.combined)) {
		result.push(data.combined);
	}

	return result;
}

export {
	findError,
	allowedGPSKeys,
	allowedSalesTradesKeys,
	allowedDepartmentKeys,
	allowedOpCodesKeys,
	filterByInterface,
	hasPositiveBonus,
	getObjectsWithConfiguredCommission,
};
